import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faShieldHalved,
  faServer,
  faLock,
  faMagnifyingGlass,
  faCode,
} from "@fortawesome/free-solid-svg-icons";
import { featuredProjects, additionalProjects } from "../data/portfolioData";
import CaseStudyModal from "../components/CaseStudyModal";

export default function Project() {
  const [selectedCaseStudy, setSelectedCaseStudy] = useState(null);
  const [filter, setFilter] = useState("all");

  const wazuhProject = featuredProjects.find((p) => p.id === "wazuh-siem");
  const otherFeatured = featuredProjects.filter((p) => p.id !== "wazuh-siem");

  return (
    <section id="projects" className="cyber-section">
      <div className="container">
        {/* Section Header */}
        <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-5">
          <div>
            <div className="section-label">PRACTICAL IMPLEMENTATIONS</div>
            <h2 className="section-title mb-2">Projects &amp; Security Engineering</h2>
            <p className="section-subtitle mb-0">
              Deep-dive case studies detailing defensive SIEM deployments, application penetration
              testing, infrastructure hardening, and full-stack software development.
            </p>
          </div>

          {/* Filter Pills */}
          <div className="d-flex gap-2">
            <button
              type="button"
              onClick={() => setFilter("all")}
              className={`btn font-mono ${filter === "all" ? "btn-cyber-primary" : "btn-cyber-secondary"}`}
              style={{ fontSize: "0.8rem", padding: "6px 14px" }}
            >
              All Projects
            </button>
            <button
              type="button"
              onClick={() => setFilter("security")}
              className={`btn font-mono ${filter === "security" ? "btn-cyber-primary" : "btn-cyber-secondary"}`}
              style={{ fontSize: "0.8rem", padding: "6px 14px" }}
            >
              Security Only
            </button>
            <button
              type="button"
              onClick={() => setFilter("engineering")}
              className={`btn font-mono ${filter === "engineering" ? "btn-cyber-primary" : "btn-cyber-secondary"}`}
              style={{ fontSize: "0.8rem", padding: "6px 14px" }}
            >
              Engineering
            </button>
          </div>
        </div>

        {/* 1. FEATURED PROJECT: WAZUH SIEM (Always highlighted when not filtering to pure engineering) */}
        {filter !== "engineering" && wazuhProject && (
          <div className="mb-5">
            <div
              className="cyber-card p-4 p-lg-5"
              style={{
                background: "linear-gradient(135deg, rgba(14, 17, 22, 0.95) 0%, rgba(8, 10, 13, 0.98) 100%)",
                border: "1px solid rgba(56, 189, 248, 0.35)",
                boxShadow: "0 20px 40px -15px rgba(0, 0, 0, 0.8), 0 0 30px -10px var(--accent-glow)",
              }}
            >
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div className="d-flex align-items-center gap-2">
                  <span
                    className="badge font-mono"
                    style={{
                      background: "rgba(56, 189, 248, 0.15)",
                      color: "var(--accent)",
                      border: "1px solid rgba(56, 189, 248, 0.4)",
                      fontSize: "0.75rem",
                      padding: "5px 12px",
                    }}
                  >
                    ★ FEATURED DEFENSIVE CASE STUDY
                  </span>
                  <span className="font-mono text-muted" style={{ fontSize: "0.78rem" }}>
                    {wazuhProject.category}
                  </span>
                </div>

                <span className="font-mono text-muted" style={{ fontSize: "0.75rem" }}>
                  Active Defense Architecture
                </span>
              </div>

              <div className="row g-4 align-items-center">
                <div className="col-lg-7">
                  <h3
                    style={{
                      fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
                      fontWeight: 800,
                      color: "var(--text-primary)",
                      letterSpacing: "-0.02em",
                      marginBottom: "16px",
                    }}
                  >
                    {wazuhProject.name}
                  </h3>

                  <p style={{ fontSize: "1.05rem", color: "var(--text-primary)", lineHeight: 1.6, marginBottom: "20px" }}>
                    {wazuhProject.shortDescription}
                  </p>

                  <div className="mb-3">
                    <div className="font-mono text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>
                      Security Problem
                    </div>
                    <p style={{ fontSize: "0.9rem", color: "var(--text-secondary)", lineHeight: 1.6 }}>
                      {wazuhProject.problem}
                    </p>
                  </div>

                  <div className="mb-4">
                    <div className="font-mono text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>
                      My Contribution
                    </div>
                    <p style={{ fontSize: "0.9rem", color: "var(--text-secondary)", lineHeight: 1.6 }}>
                      {wazuhProject.contribution}
                    </p>
                  </div>

                  {/* Technologies */}
                  <div className="d-flex flex-wrap gap-2 mb-4">
                    {wazuhProject.technologies.map((tech, idx) => (
                      <span key={idx} className="cyber-tag font-mono">
                        {tech}
                      </span>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => setSelectedCaseStudy(wazuhProject)}
                    className="btn-cyber-primary"
                  >
                    VIEW CASE STUDY
                    <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: "0.85rem" }} />
                  </button>
                </div>

                {/* Right Column: Wazuh Architecture & Retention Policy */}
                <div className="col-lg-5">
                  <div
                    className="p-4 rounded-3"
                    style={{
                      background: "var(--bg-surface-elevated)",
                      border: "1px solid var(--border-subtle)",
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="font-mono text-uppercase text-muted" style={{ fontSize: "0.72rem", letterSpacing: "0.08em" }}>
                        PIPELINE ARCHITECTURE
                      </div>
                      <FontAwesomeIcon icon={faServer} style={{ color: "var(--accent)" }} />
                    </div>

                    {/* Pipeline vertical steps */}
                    <div className="d-flex flex-column gap-2 mb-4">
                      {wazuhProject.architecture.map((step, idx) => (
                        <div
                          key={idx}
                          className="d-flex align-items-center justify-content-between p-2 rounded-2"
                          style={{
                            background: "rgba(255, 255, 255, 0.02)",
                            border: "1px solid rgba(255, 255, 255, 0.05)",
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <span className="font-mono" style={{ color: "var(--accent)", fontSize: "0.75rem" }}>
                              0{idx + 1}
                            </span>
                            <span style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--text-primary)" }}>
                              {step.name}
                            </span>
                          </div>
                          <span className="font-mono text-muted" style={{ fontSize: "0.72rem" }}>
                            {step.desc}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Retention Policy Blueprint */}
                    <div className="pt-3" style={{ borderTop: "1px solid var(--border-subtle)" }}>
                      <div className="font-mono text-uppercase text-muted mb-2" style={{ fontSize: "0.72rem", letterSpacing: "0.08em" }}>
                        INDEX RETENTION LIFECYCLE
                      </div>
                      <div className="row g-2">
                        {wazuhProject.retentionPolicy.map((tier, idx) => (
                          <div key={idx} className="col-4 text-center">
                            <div
                              className="p-2 rounded-2"
                              style={{
                                background: "rgba(255, 255, 255, 0.03)",
                                border: `1px solid ${tier.color}50`,
                              }}
                            >
                              <div className="font-mono fw-bold" style={{ color: tier.color, fontSize: "0.85rem" }}>
                                {tier.tier}
                              </div>
                              <div className="font-mono text-muted" style={{ fontSize: "0.7rem" }}>
                                {tier.duration}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 2. OTHER MAJOR SECURITY CASE STUDIES (VAPT, System Hardening, OSINT) */}
        {filter !== "engineering" && (
          <div className="row g-4 mb-5">
            {otherFeatured.map((item) => (
              <div key={item.id} className="col-lg-4 col-md-6">
                <div
                  className="cyber-card h-100 d-flex flex-column justify-content-between p-4"
                  style={{ background: "var(--bg-surface)" }}
                >
                  <div>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span className="section-label mb-0" style={{ fontSize: "0.68rem" }}>
                        {item.category}
                      </span>
                      <FontAwesomeIcon
                        icon={item.id.includes("vapt") ? faShieldHalved : item.id.includes("hardening") ? faLock : faMagnifyingGlass}
                        style={{ color: "var(--accent)", opacity: 0.8 }}
                      />
                    </div>

                    <h3 style={{ fontSize: "1.3rem", fontWeight: 700, marginBottom: "12px", color: "var(--text-primary)" }}>
                      {item.name}
                    </h3>

                    <p style={{ fontSize: "0.88rem", color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: "18px" }}>
                      {item.shortDescription}
                    </p>

                    <div className="mb-3">
                      <div className="font-mono text-muted text-uppercase mb-1" style={{ fontSize: "0.72rem" }}>
                        Problem Addressed
                      </div>
                      <p style={{ fontSize: "0.82rem", color: "var(--text-secondary)", lineHeight: 1.5 }}>
                        {item.problem}
                      </p>
                    </div>

                    <div className="mb-4">
                      <div className="font-mono text-muted text-uppercase mb-1" style={{ fontSize: "0.72rem" }}>
                        My Contribution
                      </div>
                      <p style={{ fontSize: "0.82rem", color: "var(--text-secondary)", lineHeight: 1.5 }}>
                        {item.contribution}
                      </p>
                    </div>
                  </div>

                  <div>
                    <div className="d-flex flex-wrap gap-1 mb-4">
                      {item.technologies.slice(0, 4).map((tech, idx) => (
                        <span key={idx} className="cyber-tag font-mono">
                          {tech}
                        </span>
                      ))}
                      {item.technologies.length > 4 && (
                        <span className="cyber-tag font-mono text-muted">
                          +{item.technologies.length - 4}
                        </span>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => setSelectedCaseStudy(item)}
                      className="btn-cyber-secondary w-100"
                      style={{ fontSize: "0.85rem", padding: "10px 16px" }}
                    >
                      VIEW CASE STUDY
                      <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: "0.75rem" }} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 3. ADDITIONAL ENGINEERING & PRODUCTION PROJECTS */}
        {filter !== "security" && (
          <div className="pt-3">
            <div className="d-flex align-items-center gap-2 mb-4">
              <FontAwesomeIcon icon={faCode} style={{ color: "var(--accent)" }} />
              <h3 style={{ fontSize: "1.4rem", fontWeight: 700, margin: 0 }}>
                Web Platforms &amp; Software Utilities
              </h3>
            </div>

            <div className="row g-4">
              {additionalProjects.map((proj) => (
                <div key={proj.id} className="col-lg-4 col-md-6">
                  <div
                    className="cyber-card h-100 d-flex flex-column justify-content-between p-3"
                    style={{ background: "var(--bg-surface)" }}
                  >
                    <div>
                      {/* Image Thumbnail */}
                      <div
                        className="rounded-2 overflow-hidden mb-3"
                        style={{
                          height: "180px",
                          background: "var(--bg-surface-elevated)",
                          border: "1px solid var(--border-subtle)",
                        }}
                      >
                        <img
                          src={proj.image}
                          alt={proj.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "top",
                            transition: "var(--transition)",
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </div>

                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="font-mono text-muted" style={{ fontSize: "0.72rem" }}>
                          {proj.category}
                        </span>
                        <span className="font-mono text-muted" style={{ fontSize: "0.72rem" }}>
                          {proj.date}
                        </span>
                      </div>

                      <h4 style={{ fontSize: "1.15rem", fontWeight: 700, marginBottom: "8px", color: "var(--text-primary)" }}>
                        {proj.name}
                      </h4>

                      <p style={{ fontSize: "0.85rem", color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: "16px" }}>
                        {proj.description}
                      </p>
                    </div>

                    <div>
                      <div className="d-flex flex-wrap gap-1">
                        {proj.technologies.map((t, idx) => (
                          <span key={idx} className="cyber-tag font-mono">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Modal Case Study Viewer */}
        {selectedCaseStudy && (
          <CaseStudyModal
            project={selectedCaseStudy}
            onClose={() => setSelectedCaseStudy(null)}
          />
        )}
      </div>
    </section>
  );
}
